import { useCallback, useEffect, useState } from "react";
import { DatabaseBackup, History, RefreshCw, RotateCcw, ShieldCheck, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import PinConfirm from "../components/PinConfirm";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FibiLoading } from "../components/FibiLoading";
import { DatePicker } from "../components/DatePicker";

export default function BackupRestore() {
  const [backups, setBackups] = useState(null);
  const [logs, setLogs] = useState([]);
  const [backingUp, setBackingUp] = useState(false);
  const [health, setHealth] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");

  const load = useCallback(async () => {
    try {
      const [b, l, h] = await Promise.all([api.get("/backups", { params: { q, date } }), api.get("/backups/restore-logs"), api.get("/health-check")]);
      setBackups(b.data);
      setLogs(l.data);
      setHealth(h.data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  }, [q, date]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const runBackup = async () => {
    setBackingUp(true);
    try {
      await api.post("/backups/run", {});
      toast.success("Backup semua router dimulai di background — muat ulang daftar dalam ±1 menit.");
      setTimeout(load, 30000);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBackingUp(false);
    }
  };

  const doRestore = async () => {
    try {
      await api.post(`/backups/${restoreTarget.id}/restore`);
      toast.success(`Restore ke ${restoreTarget.router_name} dimulai di background — cek Riwayat Restore di bawah.`);
      setRestoreTarget(null);
      setTimeout(load, 15000);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Backup & Restore</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Kode voucher & PPP secret semua cabang dibackup otomatis tiap hari jam 02.00 WIB dan disimpan 14 hari — antisipasi MikroTik mati / data hilang.
          </p>
        </div>
        <Button onClick={runBackup} disabled={backingUp} data-testid="btn-run-backup">
          <DatabaseBackup className="w-4 h-4 mr-1.5" /> {backingUp ? "Memulai..." : "Backup Sekarang"}
        </Button>
      </div>

      {health && (
        <Card className="bg-card border-border shadow-sm" data-testid="health-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><WifiOff className="w-4 h-4 text-rose-500" /> Kesehatan Koneksi Cabang</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2 text-xs font-mono" data-testid="health-summary">
              <span className="px-2.5 py-1 rounded-full border bg-emerald-500/10 text-emerald-500 border-emerald-500/20">Online: {health.summary?.ok ?? 0}</span>
              <span className="px-2.5 py-1 rounded-full border bg-rose-500/10 text-rose-400 border-rose-500/20">Tidak Online: {health.summary?.unreachable ?? 0}</span>
              <span className="px-2.5 py-1 rounded-full border bg-amber-500/10 text-amber-500 border-amber-500/20">Gagal Login: {health.summary?.["auth-failed"] ?? 0}</span>
            </div>
            <div className="text-xs text-muted-foreground">{health.checked_at ? `Terakhir dicek: ${fmtDateTime(health.checked_at)}` : "Belum pernah dites — klik Tes Koneksi Masal"}</div>
            <div className="divide-y divide-border max-h-64 overflow-y-auto rounded-xl border border-border" data-testid="health-offline-list">
              {(health.offline || []).map((o) => (
                <div key={o.id} className="px-3 py-2 text-xs flex items-center gap-2" data-testid={`health-offline-${o.router_name}`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${o.status === "auth-failed" ? "bg-amber-500" : "bg-rose-500"}`} />
                  <span className="font-medium flex-1">{o.router_name}</span>
                  <span className="text-muted-foreground font-mono">{o.status === "auth-failed" ? "gagal login" : "tidak online"}</span>
                </div>
              ))}
              {health.checked_at && !(health.offline || []).length && <div className="px-3 py-6 text-center text-xs text-emerald-500">Semua cabang online</div>}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="backup-list-card">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-500" /> Riwayat Backup
            <Button size="icon" variant="ghost" onClick={load} title="Muat ulang" data-testid="btn-refresh-backups"><RefreshCw className="w-4 h-4" /></Button>
          </CardTitle>
          <div className="flex flex-wrap gap-2 pt-2">
            <Input placeholder="Cari nama router/cabang..." value={q} onChange={(e) => setQ(e.target.value)} className="w-56" data-testid="backup-filter-router" />
            <DatePicker value={date} onChange={(v) => setDate(v)} className="w-44" testid="backup-filter-date" />
            {(q || date) && <Button variant="ghost" size="sm" onClick={() => { setQ(""); setDate(""); }} data-testid="backup-filter-clear">Reset</Button>}
            <span className="text-xs text-muted-foreground self-center">Tanpa filter hanya 300 backup terbaru yang tampil — ketik nama router untuk melihat semua tanggal (14 hari).</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead><TableHead>Router</TableHead><TableHead>Voucher</TableHead>
                <TableHead>PPP</TableHead><TableHead>Status</TableHead><TableHead>Pemicu</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {backups === null && <TableRow><TableCell colSpan={7} className="p-0"><FibiLoading text="FIBI sedang memuat data..." size={20} /></TableCell></TableRow>}
              {backups !== null && !backups.length && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground" data-testid="backup-empty">Belum ada backup</TableCell></TableRow>}
              {(backups || []).map((b) => (
                <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{fmtDateTime(b.created_at)}</TableCell>
                  <TableCell className="font-medium">{b.router_name}</TableCell>
                  <TableCell className="font-mono">{b.vouchers_count ?? "-"}</TableCell>
                  <TableCell className="font-mono">{b.ppp_count ?? "-"}</TableCell>
                  <TableCell data-testid={`backup-status-${b.id}`}>
                    {b.status === "ok" ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-mono border bg-emerald-500/10 text-emerald-500 border-emerald-500/20">OK</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs font-mono border bg-rose-500/10 text-rose-400 border-rose-500/20" title={b.error}>GAGAL</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{b.triggered_by === "cron" ? "Otomatis" : b.triggered_by}</TableCell>
                  <TableCell className="text-right">
                    {b.status === "ok" && (
                      <Button size="sm" variant="outline" onClick={() => { setRestoreTarget(b); setConfirmOpen(true); }} data-testid={`btn-restore-${b.id}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-card border-border shadow-sm" data-testid="restore-logs-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><History className="w-4 h-4 text-sky-500" /> Riwayat Restore</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border max-h-72 overflow-y-auto" data-testid="restore-logs">
            {logs.map((l) => (
              <div key={l.id} className="px-4 py-2.5 text-xs flex items-center gap-3" data-testid={`restore-log-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">
                    {l.router_name || "Router"} — {l.status === "ok"
                      ? `voucher +${l.vouchers_added} (lewati ${l.vouchers_skipped}), PPP +${l.ppp_added} (lewati ${l.ppp_skipped})`
                      : <span className="text-rose-400">Gagal: {l.error}</span>}
                  </div>
                  <div className="text-muted-foreground font-mono">{fmtDateTime(l.created_at)} • oleh {l.triggered_by}</div>
                </div>
              </div>
            ))}
            {!logs.length && <div className="px-4 py-8 text-center text-xs text-muted-foreground">Belum ada riwayat restore</div>}
          </div>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setRestoreTarget(null); }}>
        <DialogContent data-testid="dialog-restore-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RotateCcw className="w-5 h-5 text-amber-500" /> Restore backup ke {restoreTarget?.router_name}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {restoreTarget?.vouchers_count ?? 0} voucher & {restoreTarget?.ppp_count ?? 0} PPP secret hasil backup {restoreTarget ? fmtDateTime(restoreTarget.created_at) : ""} akan ditambahkan ke router. Data yang namanya sudah ada akan dilewati (tidak ditimpa).
            </p>
            <p className="text-sm font-semibold text-amber-600" data-testid="restore-confirm-question">Apakah benar akan direstore? Anda yakin?</p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" onClick={() => { setConfirmOpen(false); setRestoreTarget(null); }} data-testid="btn-restore-cancel">Batal</Button>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => { setConfirmOpen(false); setPinOpen(true); }} data-testid="btn-restore-yakin">Ya, Saya Yakin</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PinConfirm
        open={pinOpen}
        onOpenChange={setPinOpen}
        title={`PIN wajib untuk restore ${restoreTarget?.router_name}`}
        description="Masukkan PIN Anda untuk menjalankan restore. Tanpa PIN yang benar, restore tidak akan dijalankan."
        confirmLabel="Restore Sekarang"
        destructive
        onConfirm={doRestore}
        testid="pin-restore-backup"
      />
    </div>
  );
}
